'use client'

import { format } from 'date-fns'
import { CalendarIcon } from 'lucide-react'

import { cn } from '../cn'
import { Button } from './button'
import { Calendar } from './calendar'
import { Input } from './input'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

export interface DatePickerProps {
  value: string
  onChange: (value: string) => void
  className?: string
  placeholder?: string
  disabled?: boolean
  /** Blocks individual days in the calendar (e.g. past order deadlines, no-service days). */
  disabledDates?: (date: Date) => boolean
}

export function DatePicker({
  value,
  onChange,
  className,
  placeholder = 'Pick a date',
  disabled,
  disabledDates,
}: DatePickerProps) {
  const date = value ? new Date(value + 'T00:00:00') : undefined

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'h-8 justify-start gap-2 px-3 text-left text-sm font-normal',
            !date && 'text-muted-foreground',
            className,
          )}>
          <CalendarIcon className="h-3.5 w-3.5" />
          {date ? format(date, 'MMM d, yyyy') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          disabled={disabledDates}
          onSelect={(d) => {
            if (d) {
              const yyyy = d.getFullYear()
              const mm = String(d.getMonth() + 1).padStart(2, '0')
              const dd = String(d.getDate()).padStart(2, '0')
              onChange(`${yyyy}-${mm}-${dd}`)
            }
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

export interface LocalDateTimePickerProps {
  date: string
  hour: number
  minute: number
  onDateChange: (value: string) => void
  onHourChange: (value: number) => void
  onMinuteChange: (value: number) => void
  timezoneLabel?: string
}

export function LocalDateTimePicker({
  date,
  hour,
  minute,
  onDateChange,
  onHourChange,
  onMinuteChange,
  timezoneLabel = 'UTC',
}: LocalDateTimePickerProps) {
  const dateObj = date ? new Date(date + 'T00:00:00') : undefined

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            'h-8 justify-start gap-2 px-3 text-left text-sm font-normal',
            !dateObj && 'text-muted-foreground',
          )}>
          <CalendarIcon className="h-3.5 w-3.5" />
          {dateObj
            ? `${format(dateObj, 'MMM d, yyyy')} at ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${timezoneLabel}`
            : 'Pick date & time'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={dateObj}
          onSelect={(d) => {
            if (d) {
              const yyyy = d.getFullYear()
              const mm = String(d.getMonth() + 1).padStart(2, '0')
              const dd = String(d.getDate()).padStart(2, '0')
              onDateChange(`${yyyy}-${mm}-${dd}`)
            }
          }}
        />
        <div className="flex items-center gap-2 border-t border-border px-3 py-2">
          <span className="text-xs text-muted-foreground">Time ({timezoneLabel})</span>
          <Input
            type="time"
            value={`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`}
            onChange={(e) => {
              const [h, m] = e.target.value.split(':').map(Number)
              onHourChange(h)
              onMinuteChange(m)
            }}
            className="h-7 w-auto text-sm"
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
